import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Optional inline prefix slot (icon, currency symbol, etc.) */
  prefix?: ReactNode;
}

/**
 * TextInput — v7 text input with optional inline prefix slot.
 *
 * Wraps the native input in a span so the focus ring + border render around
 * the prefix-and-input pair (focus-within). The native input is borderless
 * and inherits the wrapper's typography.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ prefix, className = '', ...props }, ref) => (
    // MKS-493 — `rounded-ms-control` defaults to 0.375rem, the `rounded-md`
    // it replaces; Sparkle lifts it to 12px. The border follows
    // `--ms-border`, which Sparkle points at its input hairline.
    <span
      className={`ms-input flex items-center gap-2 px-3 py-2 bg-white rounded-ms-control border border-ms-border focus-within:ring-2 focus-within:ring-ms-orange/40 ${className}`}
    >
      {prefix && <span className="text-ms-text-muted text-[12px]">{prefix}</span>}
      <input
        ref={ref}
        className="flex-1 bg-transparent outline-none text-[12.5px] text-ms-text-strong placeholder:text-ms-text-muted"
        {...props}
      />
    </span>
  )
);
TextInput.displayName = 'TextInput';
